import {useRef, useState} from 'react'
import {Text} from '@/components/tailwind/text'
import {Textarea} from '@/components/tailwind/textarea'
import LoaderSpinner from '@/components/loader-spinner'

interface InlineEditableCellProps {
    value: string | undefined
    onSave: (newValue: string | null) => Promise<void>
    placeholder?: string
}

export default function InlineEditableCell({value, onSave, placeholder}: InlineEditableCellProps) {
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(value ?? '')
    const [isSaving, setIsSaving] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    function handleClick() {
        setEditValue(value ?? '')
        setIsEditing(true)
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus()
                textareaRef.current.select()
            }
        }, 0)
    }

    async function handleSave() {
        setIsSaving(true)
        try {
            const trimmed = editValue.trim()
            await onSave(trimmed.length > 0 ? trimmed : null)
        } finally {
            setIsSaving(false)
            setIsEditing(false)
        }
    }

    function handleCancel() {
        setIsEditing(false)
        setEditValue(value ?? '')
    }

    async function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            await handleSave()
        } else if (event.key === 'Escape') {
            handleCancel()
        }
    }

    async function handleBlur() {
        await handleSave()
    }

    if (isSaving) {
        return <LoaderSpinner/>
    }

    if (isEditing) {
        return (
            <Textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                rows={2}
                className="min-w-32"
            />
        )
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            className="cursor-pointer min-h-8 rounded px-1 hover:bg-gray-100 dark:hover:bg-zinc-700"
            title={placeholder}
        >
            {value
                ? <Text className="whitespace-pre-wrap">{value}</Text>
                : <Text className="italic text-gray-400 dark:text-zinc-500">{placeholder ?? '–'}</Text>
            }
        </div>
    )
}
